import React from "react";
import { Tooltip } from "antd";
import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";

export interface StatsInfoCardProps {
  icon: LucideIcon | ReactNode;
  title: string;
  value: string | number;
  valuePrefix?: string | number;
  /** Full exact amount shown on hover when value is abbreviated. */
  tooltip?: string;
  change?: string | number;
  changeColor?: "emerald" | "red" | "blue" | "yellow" | "gray";
  iconBg?: string;
  iconColor?: string;
  className?: string;
  // Anchor for the onboarding tour to spotlight this single card.
  dataTour?: string;
}

// Semantic tokens, not raw palette rungs: `text-emerald-600` is 3.3:1 on the
// dark surface-elevated, and the raw rungs don't shift with the theme at all.
const changeColorClasses = {
  emerald: "text-success",
  red: "text-danger",
  blue: "text-info",
  yellow: "text-warning",
  gray: "text-secondary",
};

export default function StatsInfoCard({
  icon,
  title,
  value,
  change,
  valuePrefix,
  tooltip,
  changeColor = "emerald",
  iconBg = "bg-pink-50",
  iconColor = "text-pink-600",
  className = "",
  dataTour,
}: StatsInfoCardProps) {
  const changeClass = changeColorClasses[changeColor];

  const valueNode = (
    <div className="text-metric whitespace-nowrap">
      {valuePrefix && (
        <span className="text-sm sm:text-base text-tertiary mr-1">
          {valuePrefix}
        </span>
      )}
      {value}
    </div>
  );

  return (
    <div
      data-tour={dataTour}
      className={`flex items-center gap-3 sm:gap-4 justify-start rounded-xl border border-border bg-surface-elevated p-3 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:justify-start ${className}`}
    >
      <div
        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}
      >
        {!React.isValidElement(icon) ? (
          React.createElement(icon as React.ComponentType<{ className: string }>, { className: `w-5 h-5 sm:w-6 sm:h-6 ${iconColor}` })
        ) : (
          <div
            className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor} flex items-center justify-center`}
          >
            {icon}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-tertiary mb-0.5 sm:mb-1 leading-snug">
          {title}
        </div>
        <div className="flex items-baseline gap-2">
          {tooltip ? (
            <Tooltip title={tooltip}>
              <span className="cursor-default">
                {valueNode}
              </span>
            </Tooltip>
          ) : (
            valueNode
          )}
          {change && (
            <div
              className={`text-xs ${changeClass} font-medium`}
            >
              {change}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
